<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import DrawerHeader from './DrawerHeader/index.vue'
import { injectDrawerContext } from './DrawerContext'
import { useSidebarStore } from '~~/src/stores/sidebar'
import { useLeftSidebarStore } from '~~/src/stores/leftSidebar'
import type { ResizeOptions } from '~~/src/composables/useResize'
import { MAIN_SCROLL_MIN_WIDTH } from '~~/src/layouts/default.vue'

defineOptions({ name: 'Drawer' })

const SCROLL_STORAGE_KEY = 'drawerScrollPosition'
const WIDTH_LEFT_SIDEBAR_OPEN = 281
const WIDTH_LEFT_SIDEBAR_CLOSED = 60
const MIN_SAFE_WIDTH = 320
const MAX_SAFE_WIDTH = 600

const { state, toggleSidebar, updateScrollPosition } = useSidebarStore()
const scrollableContainer = ref<HTMLElement | null>(null)
const isRestoring = ref(false)
const lastValidScroll = ref<number | null>(null)

const restoreScroll = async () => {
  await nextTick()
  if (scrollableContainer.value && !isRestoring.value) {
    isRestoring.value = true
    const savedScroll = sessionStorage.getItem(SCROLL_STORAGE_KEY)
    if (savedScroll) {
      const scrollValue = parseInt(savedScroll, 10)
      scrollableContainer.value.scrollTop = scrollValue
      setTimeout(() => {
        isRestoring.value = false
      }, 100)
    }
    else {
      isRestoring.value = false
    }
  }
}

onMounted(restoreScroll)

watch([() => state.mode, () => state.isOpen], async () => {
  if (state.isOpen && state.mode !== 'folder') {
    await nextTick()
    await restoreScroll()
  }
}, { immediate: true })

const updateScroll = useThrottleFn((event: Event) => {
  if (!isRestoring.value && state.mode !== 'folder') {
    const currentTarget = event.currentTarget as HTMLElement
    const scrollPosition = currentTarget.scrollTop
    updateScrollPosition(scrollPosition)
    sessionStorage.setItem('drawerScrollPosition', scrollPosition.toString())
    lastValidScroll.value = scrollPosition
  }
}, 50)

watch(() => state.mode, (newMode, oldMode) => {
  if (oldMode === 'folder' && newMode !== 'folder' && lastValidScroll.value !== null) {
    sessionStorage.setItem('drawerScrollPosition', lastValidScroll.value.toString())
    if (scrollableContainer.value)
      scrollableContainer.value.scrollTop = lastValidScroll.value
  }
})

const leftSidebar = useLeftSidebarStore()

useEventListener('keydown', (event) => {
  if ((event.key === '/' && event.altKey))
    toggleSidebar()
})

const resizeOptions = ref<ResizeOptions>({
  min: '420px',
  max: '600px',
  onReachMin() {
    if (state.isOpen)
      toggleSidebar(false)
  },
})

const { elementRef, current, isResizing } = useResize(resizeOptions, 'drawer-sidebar')

const isSmall = computed(() => parseValuesToPX(current.value) < 768)

injectDrawerContext({
  isSmall,
})

const { width: windowWidth } = useWindowSize()
const safeMaxWidth = ref('100%')

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const calculateSafeWidth = () => {
  if (process.server || isMobile.value)
    return '100%'
  const safeMaxWidthVal = windowWidth.value - (MAIN_SCROLL_MIN_WIDTH + (leftSidebar.isOpen ? WIDTH_LEFT_SIDEBAR_OPEN : WIDTH_LEFT_SIDEBAR_CLOSED))
  return (safeMaxWidthVal < MIN_SAFE_WIDTH || safeMaxWidthVal > MAX_SAFE_WIDTH) ? '100%' : `${safeMaxWidthVal}px`
}

watch([windowWidth, () => leftSidebar.isOpen], () => {
  safeMaxWidth.value = calculateSafeWidth()
})

onMounted(() => {
  safeMaxWidth.value = calculateSafeWidth()
})
</script>

<template>
  <div
    class="flex items-stretch max-h-full h-full isolate z-10 transition-all"
    :style="{ maxWidth: safeMaxWidth }"
  >
    <div
      ref="elementRef"
      class="relative border-r-softGray border-r-2 border-r-darkGray border-l-softGray after:content-[''] after:absolute after:top-0 after:w-0 after:h-full after:bg-softGray after:z-10 after:-translate-x-[50%]"
      :class="{
        'z-999!': isSmall,
        'after:w-[5px]! after:bg-darkGray/50! border-r-transparent!': state.isOpen && isResizing,
        'hover:after:w-[5px]! hover:cursor-col-resize hover:after:bg-darkGray/50! hover:border-r-transparent': state.isOpen,
      }"
    />
    <div
      :class="[$style.wrapper]"
      :aria-expanded="state.isOpen"
    >
      <div class="flex flex-col justify-center h-full">
        <slot name="header">
          <DrawerHeader />
        </slot>
        <div
          ref="scrollableContainer"
          :aria-expanded="state.isOpen"
          :class="[$style.container, $style['no-scrollbar']]"
          class="
          min-w-64
          max-h-full
          h-full
          w-full
          overflow-y-auto
          overflow-x-hidden"
          @scroll="updateScroll($event)"
        >
          <div
            :style="{ display: state.isOpen ? 'block' : 'none' }"
          >
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.wrapper {
  @apply flex flex-col min-h-full bg-white z-2 items-stretch max-h-full h-full;
  &[aria-expanded='true'] {
    @apply overflow-x-auto;
    width: v-bind(current);
  }
  &[aria-expanded='false'] {
    @apply w-15 translate-x-100 sm:translate-x-0;
  }
  &::-webkit-scrollbar {
    display: none;
  }
  transition: width 0.3s ease-out;
}

.container {
  &[aria-expanded='false'] {
    @apply translate-x-50;
  }
  @apply translate-x-0 sm:p-0 bg-white;
  transition: transform 0.4s ease-out;
  overflow-y: auto;
  max-height: 100vh;
}

.no-scrollbar {
  scrollbar-width: thin;
  scrollbar-gutter: stable;
  overflow-y: auto;
}
</style>
